import { MOCK_CONTEST_TEAM, MOCK_ROSTER } from './gameplay.mock';
import type { ContestEntry, ContestTeam, ContestTeamSummary, TeamPlayer } from '@/types';
import { toTeamPlayer } from '@/lib/team-builder';

/**
 * Session-scoped memory for the demo.
 *
 * With no API every fixture is a constant, so entering a contest would be
 * followed immediately by "you have not entered a contest" — which reads as a
 * broken app rather than as missing data. Remembering the entry lets the whole
 * journey be walked end to end.
 *
 * Held in `sessionStorage` rather than a module variable so a refresh mid-demo
 * does not silently lose the team, and rather than `localStorage` so closing
 * the tab still hands the next person a clean demo. Nothing here is real: none
 * of it is ever sent to the API.
 */

// Versioned: an entry saved before the bench existed has no bench to render.
const KEY = 'matchday-entry-v2';

interface DemoEntry {
  contestId: string;
  team: ContestTeam;
}

const readEntry = (): DemoEntry | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as DemoEntry) : null;
  } catch {
    // Private browsing, or a payload from an older shape.
    return null;
  }
};

const writeEntry = (value: DemoEntry): DemoEntry => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Storage refused; the demo still works for this page view.
  }
  return value;
};


/** Build a team from a submitted entry, so the demo shows the actual picks. */
export function rememberEntry(contestId: string, submitted: ContestEntry): ContestTeam {
  const byId = new Map(MOCK_ROSTER.map((player) => [player.playerId, player]));

  const resolve = (ids: readonly number[], withArmband: boolean): TeamPlayer[] =>
    ids.flatMap((playerId) => {
      const source = byId.get(playerId);
      if (!source) return [];
      // The bench never carries an armband, as on the API.
      return withArmband
        ? [toTeamPlayer(source, submitted.captainPlayerId, submitted.vicePlayerId)]
        : [toTeamPlayer(source, null, null)];
    });

  const players = resolve(submitted.startingPlayerIds, true);
  const benchPlayers = resolve(submitted.benchPlayerIds, false);

  const team: ContestTeam = {
    ...MOCK_CONTEST_TEAM,
    id: `team_${contestId}`,
    contestId,
    teamName: submitted.teamName?.trim() || MOCK_CONTEST_TEAM.teamName,
    captainPlayerId: submitted.captainPlayerId,
    vicePlayerId: submitted.vicePlayerId,
    // A fresh entry has played nothing yet.
    points: 0,
    transfersUsed: 0,
    transfersRemaining: MOCK_CONTEST_TEAM.maxTransfers,
    squadComplete: players.length === 11 && benchPlayers.length === 4,
    ...squadIds(players, benchPlayers),
    players,
    benchPlayers,
  };

  return writeEntry({ contestId, team }).team;
}

/** The team entered during this demo session, if any. */
export const demoTeam = (): ContestTeam | null => readEntry()?.team ?? null;

/** The contest entered during this demo session, if any. */
export const demoEnteredContestId = (): string | null => readEntry()?.contestId ?? null;

/** The id lists the API sends alongside the detail. */
const squadIds = (players: readonly TeamPlayer[], bench: readonly TeamPlayer[]) => {
  const startingPlayerIds = players.map((player) => player.playerId);
  const benchPlayerIds = bench.map((player) => player.playerId);

  return {
    startingPlayerIds,
    benchPlayerIds,
    playerIds: [...startingPlayerIds, ...benchPlayerIds],
  };
};

/**
 * Apply a lineup swap to the remembered team.
 *
 * The demo has no server to recompute the squad, so the swap is done here —
 * otherwise it would appear to succeed and change nothing. The two players
 * trade places: the starter takes the bench slot, the substitute the starter's.
 * Like the API, it is free until the first fixture and one transfer after.
 */
export function swapInDemoTeam(outPlayerId: number, inPlayerId: number): ContestTeam | null {
  const entry = readEntry();
  if (!entry) return null;
  const { contestId, team } = entry;

  const leaving = team.players.find((player) => player.playerId === outPlayerId);
  const joining = team.benchPlayers.find((player) => player.playerId === inPlayerId);

  // The API refuses the armband outright; the screen never offers it.
  if (!leaving || !joining || leaving.isCaptain || leaving.isViceCaptain) return team;

  const players = team.players.map((player) =>
    player.playerId === outPlayerId ? joining : player,
  );
  const benchPlayers = team.benchPlayers.map((player) =>
    player.playerId === inPlayerId ? leaving : player,
  );

  const cost = team.currentFixtureWindowIndex > 0 ? 1 : 0;

  const updated: ContestTeam = {
    ...team,
    ...squadIds(players, benchPlayers),
    players,
    benchPlayers,
    transfersUsed: team.transfersUsed + cost,
    transfersRemaining: Math.max(0, team.transfersRemaining - cost),
  };

  return writeEntry({ contestId, team: updated }).team;
}

/** Replace the remembered team wholesale, as `PUT /me/teams/:id` does. */
export function replaceDemoTeam(submitted: ContestEntry): ContestTeam {
  const entry = readEntry();
  const contestId = entry?.contestId ?? 'ct_weekly';

  // Same builder as a fresh entry — a replace is a re-pick, not a patch.
  return rememberEntry(contestId, submitted);
}

/** Set captain and vice on the remembered team. */
export function setDemoArmband(
  captainPlayerId: number,
  vicePlayerId: number,
): ContestTeam | null {
  const entry = readEntry();
  if (!entry) return null;
  const { contestId, team } = entry;

  const updated: ContestTeam = {
    ...team,
    captainPlayerId,
    vicePlayerId,
    players: team.players.map((player) => ({
      ...player,
      isCaptain: player.playerId === captainPlayerId,
      isViceCaptain: player.playerId === vicePlayerId,
    })),
  };

  return writeEntry({ contestId, team: updated }).team;
}

/** `/me/teams` shape for the remembered entry. */
export function demoTeamSummaries(): ContestTeamSummary[] {
  const entry = readEntry();
  if (!entry) return [];

  return [
    {
      id: entry.team.id,
      contestId: entry.team.contestId,
      seriesId: entry.team.seriesId,
      teamName: entry.team.teamName,
      startingPlayerIds: entry.team.startingPlayerIds,
      benchPlayerIds: entry.team.benchPlayerIds,
      playerIds: entry.team.playerIds,
      captainPlayerId: entry.team.captainPlayerId,
      vicePlayerId: entry.team.vicePlayerId,
      status: 'active',
      transferMode: 'lineup_swap',
      squadComplete: entry.team.squadComplete,
    },
  ];
}
